import {useCallback, useRef, useState} from "react";
import {useEscapeKeydown} from "@cisco-sbg-ui/magna-react";

export const DELAY_MS = 300;

// For when no configuration object is passed to `useDrawer`
export const defaultConfig = {
  initIsOpen: false
};

/**
 * Hook used for drawer UI state management that delegates the timing
 * associated with opening a new drawer when there is one already open.
 *
 * @param {boolean} initIsOpen determines if the drawer is initially opened.
 * @returns {Object} Object contains state helpers for updating drawer states.
 */
const useDrawer = (config = defaultConfig) => {
  const {initIsOpen, onEscapeClose} = config;
  const closingTransitionPromise = useRef();
  // Only the last item of `waitingToBeOpened` is relevant for opening
  // the drawer, but if we ever need to preserve the history of what the
  // user has tried to open, we can get it from the array.
  const waitingToBeOpened = useRef([]);
  const [isOpen, setIsOpen] = useState(initIsOpen);
  const [data, setData] = useState();

  /**
   * A function for opening the drawer immediately without any
   * regard whatsoever to a drawer that might already be opened
   * in the UI.
   */
  const openImmediate = useCallback(
    (data) => {
      if (closingTransitionPromise.current) {
        clearTimeout(closingTransitionPromise.current);
        closingTransitionPromise.current = null;
      }
      setData(data);
      setIsOpen(true);
      return data;
    },
    [setData, setIsOpen]
  );

  /**
   * Closes the drawer and checks for any requests to re-open the
   * drawer that might have happened during the closing transition.
   */
  const close = useCallback(() => {
    if (closingTransitionPromise.current) {
      clearTimeout(closingTransitionPromise.current);
    }
    setIsOpen(false);
    const promise = new Promise((resolve) => setTimeout(resolve, DELAY_MS));
    closingTransitionPromise.current = promise;
    promise.then(() => {
      closingTransitionPromise.current = null;
      if (waitingToBeOpened.current?.length) {
        const lastRequest =
          waitingToBeOpened.current[waitingToBeOpened.current.length - 1];
        lastRequest();
        waitingToBeOpened.current = [];
      }
    });
    return promise;
  }, [setIsOpen]);

  /**
   * A function for opening the drawer with respect to a drawer that
   * might already be opened in the UI.
   *
   * @param {Any} data optional parameter for keeping drawer data
   * in state while it is opened.
   * @returns {Promise<Any>} Promise of the data that was just opened
   * alongside the drawer.
   */
  const openWhenClear = useCallback(
    async (data) => {
      return new Promise((resolve) => {
        const doOpen = () => {
          openImmediate(data);
          resolve(data);
        };

        if (closingTransitionPromise.current) {
          // There is a drawer currently being closed; wait for it
          waitingToBeOpened.current.push(doOpen);
        } else if (isOpen) {
          // There is a drawer already opened. Close, then ask to open next
          waitingToBeOpened.current.push(doOpen);
          close();
        } else {
          // No drawer is opened, we can open immediately
          doOpen();
        }
      });
    },
    [isOpen, openImmediate, close]
  );

  useEscapeKeydown({
    // Listen for escape key presses even if there is a closing
    // animation in progress. This covers the edge case where a
    // user clicks the escape key shortly after triggering a new
    // drawer to be opened while the current drawer is closing.
    isEnabled: isOpen || closingTransitionPromise.current,
    onKeydown: () => {
      close().then((data) => {
        onEscapeClose && onEscapeClose(data);
      });
    }
  });

  return {
    setData,
    data,
    isOpen,
    open: openWhenClear, // Default `open` behavior is to wait for an open drawer to close
    close,
    openImmediate
  };
};

export default useDrawer;
